import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, parseJsonSafe, formatDate } from '../utils/api';

// Software y licencias: productos (plugins / módulos), planes, versiones (zip en Drive),
// licencias con sus sitios activados, pedidos y configuración (secreto HMAC del plugin y Stripe).

const PRODUCT_TYPES = { wordpress_plugin: 'Plugin de WordPress', dolibarr_module: 'Módulo de Dolibarr', prestashop_module: 'Módulo de PrestaShop', other: 'Otro software' };
const PRODUCT_STATUS = { draft: 'Borrador', active: 'Activo', archived: 'Archivado' };
const BILLING = { one_time: 'Pago único', yearly_subscription: 'Suscripción anual' };
const LICENSE_STATUS = { active: 'Activa', expired: 'Caducada', suspended: 'Suspendida', revoked: 'Revocada', pending: 'Pendiente' };
const ORDER_STATUS = { paid: 'Pagado', pending: 'Pendiente', failed: 'Fallido', cancelled: 'Cancelado', refunded: 'Reembolsado' };
const STATUS_COLORS = { active: 'success', draft: 'secondary', archived: 'dark', paid: 'success', pending: 'warning', failed: 'danger', cancelled: 'secondary', refunded: 'info', expired: 'danger', revoked: 'danger', suspended: 'warning', deactivated: 'secondary', blocked: 'danger' };

const emptyProduct = { id: null, slug: '', name: '', type: 'wordpress_plugin', status: 'draft', description: '' };
const emptyPlan = { id: null, product_id: '', slug: '', name: '', billing_model: 'yearly_subscription', price_amount: '', currency: 'EUR', activation_limit: 1, stripe_price_id: '', is_active: true, updates_included: true, support_included: true };
const emptyRelease = { id: null, product_id: '', version: '', changelog: '', requires_wp: '', tested_up_to: '', requires_php: '', is_published: true, file: null };
const emptyLicense = { email: '', customer_name: '', product_slug: '', plan_slug: '', notes: '', source_reference: '', expires_at: '', activation_limit: '', send_email: true };
const emptyLicenseEdit = { id: null, activation_limit: 1, expires_at: '', notes: '' };

const toInputDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};
const formatBytes = (n) => {
  const v = Number(n || 0);
  if (!v) return '-';
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(0)} KB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
};

const SoftwarePage = () => {
  const token = localStorage.getItem('token');
  const isAdmin = localStorage.getItem('role') === 'admin';
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [releases, setReleases] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activations, setActivations] = useState({});
  const [expandedLicense, setExpandedLicense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterProductId, setFilterProductId] = useState('');
  const [licenseSearch, setLicenseSearch] = useState('');
  const [form, setForm] = useState({});
  const [settingsForm, setSettingsForm] = useState({});

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  }, []);

  const api = useCallback(async (url, opts = {}) => {
    const res = await apiFetch(url, { ...opts, headers: { ...headers(), ...opts.headers } });
    if (res.status === 401) { handleUnauthorized(); return null; }
    const data = await parseJsonSafe(res);
    if (!res.ok) throw new Error(data.message || 'Error');
    return data;
  }, [headers, handleUnauthorized]);
  const json = (method, body) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

  const fetchProducts = useCallback(async () => {
    try { const data = await api('/api/software/admin/products'); if (data) setProducts(data); } catch (e) { setError(e.message); }
  }, [api]);
  const fetchPlans = useCallback(async (prodId) => {
    try { const data = await api(`/api/software/admin/plans${prodId ? `?product_id=${prodId}` : ''}`); if (data) setPlans(data); } catch (e) { setError(e.message); }
  }, [api]);
  const fetchReleases = useCallback(async (prodId) => {
    if (!prodId) { setReleases([]); return; }
    try { const data = await api(`/api/software/admin/releases?product_id=${prodId}`); if (data) setReleases(data); } catch (e) { setError(e.message); }
  }, [api]);
  const fetchLicenses = useCallback(async () => {
    try { const data = await api('/api/software/admin/licenses'); if (data) setLicenses(data); } catch (e) { setError(e.message); }
  }, [api]);
  const fetchOrders = useCallback(async () => {
    try { const data = await api('/api/software/admin/orders'); if (data) setOrders(data); } catch (e) { setError(e.message); }
  }, [api]);
  const fetchSettings = useCallback(async () => {
    try { const data = await api('/api/software/admin/settings'); if (data) { setSettings(data); setSettingsForm({ mode: data.mode, test_publishable_key: data.test_publishable_key || '', live_publishable_key: data.live_publishable_key || '', test_secret_key: '', test_webhook_secret: '', live_secret_key: '', live_webhook_secret: '' }); } } catch (e) { setError(e.message); }
  }, [api]);
  const fetchActivations = useCallback(async (licenseId) => {
    try { const data = await api(`/api/software/admin/activations?license_id=${licenseId}`); if (data) setActivations(prev => ({ ...prev, [licenseId]: data })); } catch (e) { setError(e.message); }
  }, [api]);

  const loadTab = useCallback(async (t) => {
    setLoading(true);
    setError('');
    if (t === 'products') await fetchProducts();
    else if (t === 'plans') { await fetchProducts(); await fetchPlans(filterProductId); }
    else if (t === 'releases') { await fetchProducts(); if (filterProductId) await fetchReleases(filterProductId); }
    else if (t === 'licenses') { await fetchProducts(); await fetchPlans(''); await fetchLicenses(); }
    else if (t === 'orders') await fetchOrders();
    else if (t === 'settings') await fetchSettings();
    setLoading(false);
  }, [fetchProducts, fetchPlans, fetchReleases, fetchLicenses, fetchOrders, fetchSettings, filterProductId]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const openModal = (type, data) => { setModalType(type); setForm(data); setModalOpen(true); setError(''); };
  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 5000); };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (modalType === 'product') {
        const body = { slug: form.slug, name: form.name, type: form.type, status: form.status, description: form.description };
        await api(form.id ? `/api/software/admin/products/${form.id}` : '/api/software/admin/products', json(form.id ? 'PUT' : 'POST', body));
        setModalOpen(false);
        await fetchProducts();
      } else if (modalType === 'plan') {
        const body = { product_id: Number(form.product_id), slug: form.slug, name: form.name, billing_model: form.billing_model, price_amount: Number(form.price_amount), currency: form.currency, activation_limit: Number(form.activation_limit), stripe_price_id: form.stripe_price_id || '', is_active: form.is_active, updates_included: form.updates_included, support_included: form.support_included };
        await api(form.id ? `/api/software/admin/plans/${form.id}` : '/api/software/admin/plans', json(form.id ? 'PUT' : 'POST', body));
        setModalOpen(false);
        await fetchPlans(filterProductId);
      } else if (modalType === 'release') {
        if (form.id) {
          await api(`/api/software/admin/releases/${form.id}`, json('PUT', { changelog: form.changelog, requires_wp: form.requires_wp, tested_up_to: form.tested_up_to, requires_php: form.requires_php, is_published: form.is_published }));
        } else {
          if (!form.file) throw new Error('Adjunta el archivo .zip de la versión');
          const fd = new FormData();
          fd.append('file', form.file);
          ['product_id', 'version', 'changelog', 'requires_wp', 'tested_up_to', 'requires_php'].forEach((k) => fd.append(k, form[k] || ''));
          fd.append('is_published', form.is_published ? 'true' : 'false');
          await api('/api/software/admin/releases', { method: 'POST', body: fd });
        }
        setModalOpen(false);
        await fetchReleases(filterProductId);
        await fetchProducts();
      } else if (modalType === 'license') {
        const body = { email: form.email, customer_name: form.customer_name, product_slug: form.product_slug, plan_slug: form.plan_slug, notes: form.notes, source_reference: form.source_reference, send_email: form.send_email, expires_at: form.expires_at || null, activation_limit: form.activation_limit ? Number(form.activation_limit) : null };
        const created = await api('/api/software/admin/licenses/manual', json('POST', body));
        setModalOpen(false);
        await fetchLicenses();
        if (created) {
          const n = created.notice || {};
          flash(`Licencia ${created.license_key} creada. ${n.sent ? `Email enviado a ${n.email}${n.portalCreated ? ' con su acceso al área de cliente' : ''}.` : (n.skipped ? 'Sin email.' : `No se pudo enviar el email: ${n.error || ''}`)}`);
        }
      } else if (modalType === 'licenseEdit') {
        await api(`/api/software/admin/licenses/${form.id}`, json('PUT', { activation_limit: Number(form.activation_limit), expires_at: form.expires_at || '', notes: form.notes }));
        setModalOpen(false);
        await fetchLicenses();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmAnd = async (message, fn) => {
    if (!window.confirm(message)) return;
    setError('');
    try { await fn(); } catch (err) { setError(err.message); }
  };

  const deleteProduct = (p) => confirmAnd(`¿Borrar el producto «${p.name}» con sus planes y versiones? Solo es posible si no tiene licencias ni pedidos.`, async () => { await api(`/api/software/admin/products/${p.id}`, { method: 'DELETE' }); await fetchProducts(); });
  const deletePlan = (p) => confirmAnd(`¿Borrar el plan «${p.name}»?`, async () => { await api(`/api/software/admin/plans/${p.id}`, { method: 'DELETE' }); await fetchPlans(filterProductId); });
  const deleteRelease = (r) => confirmAnd(`¿Borrar la versión ${r.version}? Se elimina también el zip de Drive y los clientes dejarán de poder descargarla.`, async () => { await api(`/api/software/admin/releases/${r.id}`, { method: 'DELETE' }); await fetchReleases(filterProductId); await fetchProducts(); });
  const togglePublished = async (r) => { setError(''); try { await api(`/api/software/admin/releases/${r.id}`, json('PUT', { is_published: !r.is_published })); await fetchReleases(filterProductId); await fetchProducts(); } catch (err) { setError(err.message); } };
  const setLicenseStatus = (l, status) => {
    const labels = { suspended: 'suspender', revoked: 'revocar', active: 'reactivar' };
    confirmAnd(`¿${labels[status][0].toUpperCase() + labels[status].slice(1)} la licencia ${l.license_key}?`, async () => { await api(`/api/software/admin/licenses/${l.id}/status`, json('POST', { status })); await fetchLicenses(); });
  };
  const extendLicense = (l) => confirmAnd(`¿Prorrogar 12 meses la licencia ${l.license_key}?`, async () => { await api(`/api/software/admin/licenses/${l.id}/extend`, json('POST', { months: 12 })); await fetchLicenses(); });
  const notifyLicense = (l) => confirmAnd(`¿Enviar a ${l.customer_email} el email con la clave ${l.license_key}?${l.has_portal_access ? '' : ' Se le creará también el acceso al área de cliente.'}`, async () => { const r = await api(`/api/software/admin/licenses/${l.id}/notify`, { method: 'POST' }); flash(`Email enviado a ${r?.notice?.email || l.customer_email}.`); await fetchLicenses(); });
  const freeSite = (l, a) => confirmAnd(`¿Liberar el sitio ${a.site_url}? El cliente podrá activar la licencia en otra web.`, async () => { await api(`/api/software/admin/activations/${a.id}`, { method: 'DELETE' }); await fetchActivations(l.id); await fetchLicenses(); });

  const toggleActivations = (licenseId) => {
    if (expandedLicense === licenseId) { setExpandedLicense(null); return; }
    setExpandedLicense(licenseId);
    fetchActivations(licenseId);
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const body = { mode: settingsForm.mode, test_publishable_key: settingsForm.test_publishable_key, live_publishable_key: settingsForm.live_publishable_key };
      ['test_secret_key', 'test_webhook_secret', 'live_secret_key', 'live_webhook_secret'].forEach((k) => { if (settingsForm[k]) body[k] = settingsForm[k]; });
      await api('/api/software/admin/settings', json('PUT', body));
      flash('Configuración guardada.');
      await fetchSettings();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };
  const generateHmac = () => confirmAnd(settings?.has_hmac_secret ? 'Si generas un secreto nuevo, los plugins ya instalados con el secreto anterior dejarán de poder activar o comprobar licencias hasta que publiques una versión con el nuevo. ¿Continuar?' : '¿Generar el secreto HMAC para los plugins?', async () => { await api('/api/software/admin/settings', json('PUT', { generate_hmac: true })); await fetchSettings(); flash('Secreto generado. Cópialo en el plugin.'); });
  const copy = (text) => { if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(() => flash('Copiado al portapapeles.')).catch(() => {}); };

  const statusBadge = (status, labels = {}) => <span className={`badge bg-${STATUS_COLORS[status] || 'secondary'}`}>{labels[status] || status}</span>;

  const tabs = [
    { key: 'products', label: 'Productos' },
    { key: 'plans', label: 'Planes' },
    { key: 'releases', label: 'Versiones' },
    { key: 'licenses', label: 'Licencias' },
    { key: 'orders', label: 'Pedidos' },
    ...(isAdmin ? [{ key: 'settings', label: 'Configuración' }] : [])
  ];

  const productFilter = (
    <select className="form-select form-select-sm" style={{ width: 240 }} value={filterProductId} onChange={(e) => setFilterProductId(e.target.value)}>
      <option value="">Todos los productos</option>
      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
    </select>
  );

  const plansOfSelectedProduct = plans.filter((p) => !form.product_slug || products.find((x) => x.id === p.product_id)?.slug === form.product_slug);
  const search = licenseSearch.trim().toLowerCase();
  const visibleLicenses = licenses.filter((l) => !search || [l.license_key, l.customer_email, l.client_name, l.product_name, l.plan_name].some((v) => String(v || '').toLowerCase().includes(search)));

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Software y licencias</h2>
      </div>

      {error && <div className="alert alert-danger py-2">{error} <button className="btn-close float-end" style={{ fontSize: 10 }} onClick={() => setError('')}></button></div>}
      {notice && <div className="alert alert-success py-2">{notice}</div>}

      <ul className="nav nav-tabs mb-3">
        {tabs.map(t => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
          </li>
        ))}
      </ul>

      {loading ? <p>Cargando...</p> : (
        <>
          {tab === 'products' && (
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">Un producto es un plugin o módulo que vendes. Pasa a «Activo» cuando tenga plan y versión publicada.</span>
                  <button className="btn btn-primary btn-sm" onClick={() => openModal('product', { ...emptyProduct })}>Nuevo producto</button>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-striped align-middle">
                    <thead><tr><th>Slug</th><th>Nombre</th><th>Tipo</th><th>Estado</th><th>Versión publicada</th><th>Planes</th><th>Versiones</th><th>Licencias</th><th></th></tr></thead>
                    <tbody>
                      {!products.length ? <tr><td colSpan="9" className="text-center text-muted py-3">Sin productos. Crea el primero con «Nuevo producto».</td></tr> :
                        products.map(p => (
                          <tr key={p.id}>
                            <td><code>{p.slug}</code></td>
                            <td>{p.name}</td>
                            <td><span className="badge bg-light text-dark">{PRODUCT_TYPES[p.type] || p.type}</span></td>
                            <td>{statusBadge(p.status, PRODUCT_STATUS)}</td>
                            <td>{p.current_version || '-'}</td>
                            <td>{p.plans_count}</td>
                            <td>{p.releases_count}</td>
                            <td>{p.licenses_count}</td>
                            <td className="text-end text-nowrap">
                              <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openModal('product', { ...emptyProduct, ...p })}>Editar</button>
                              {isAdmin && !Number(p.licenses_count) && <button className="btn btn-sm btn-outline-danger" onClick={() => deleteProduct(p)}>Borrar</button>}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === 'plans' && (
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  {productFilter}
                  <button className="btn btn-primary btn-sm" onClick={() => openModal('plan', { ...emptyPlan, product_id: filterProductId })}>Nuevo plan</button>
                </div>
                <p className="text-muted mb-2">Pago único: la licencia no caduca; la fecha de caducidad marca hasta cuándo recibe actualizaciones (un año por defecto). Suscripción anual: la licencia caduca si no se renueva.</p>
                <div className="table-responsive">
                  <table className="table table-sm table-striped align-middle">
                    <thead><tr><th>Nombre</th><th>Slug</th><th>Producto</th><th>Facturación</th><th>Precio</th><th>Sitios</th><th>Precio en Stripe</th><th>Activo</th><th>Licencias</th><th></th></tr></thead>
                    <tbody>
                      {!plans.length ? <tr><td colSpan="10" className="text-center text-muted py-3">Sin planes</td></tr> :
                        plans.map(p => (
                          <tr key={p.id}>
                            <td>{p.name}</td>
                            <td><code>{p.slug}</code></td>
                            <td>{p.product_name || p.product_slug || '-'}</td>
                            <td><span className="badge bg-light text-dark">{BILLING[p.billing_model] || p.billing_model}</span></td>
                            <td>{Number(p.price_amount).toFixed(2)} {p.currency}</td>
                            <td>{p.activation_limit}</td>
                            <td><code className="text-muted" style={{ fontSize: 11 }}>{p.stripe_price_id || '-'}</code></td>
                            <td>{p.is_active ? <span className="badge bg-success">Sí</span> : <span className="badge bg-secondary">No</span>}</td>
                            <td>{p.licenses_count}</td>
                            <td className="text-end text-nowrap">
                              <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openModal('plan', { ...emptyPlan, ...p, stripe_price_id: p.stripe_price_id || '' })}>Editar</button>
                              {isAdmin && !Number(p.licenses_count) && <button className="btn btn-sm btn-outline-danger" onClick={() => deletePlan(p)}>Borrar</button>}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === 'releases' && (
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  {productFilter}
                  <button className="btn btn-primary btn-sm" disabled={!filterProductId} onClick={() => openModal('release', { ...emptyRelease, product_id: filterProductId })}>Subir versión</button>
                </div>
                {!filterProductId ? <p className="text-muted">Selecciona un producto para ver sus versiones.</p> : (
                  <>
                    <p className="text-muted mb-2">Al subir una versión publicada, los clientes con licencia la ven como actualización en su WordPress o Dolibarr. El zip se guarda en Google Drive (privado).</p>
                    <div className="table-responsive">
                      <table className="table table-sm table-striped align-middle">
                        <thead><tr><th>Versión</th><th>Archivo</th><th>Cambios</th><th>Requiere WP</th><th>Requiere PHP</th><th>Publicada</th><th>Descargas</th><th>Fecha</th><th></th></tr></thead>
                        <tbody>
                          {!releases.length ? <tr><td colSpan="9" className="text-center text-muted py-3">Sin versiones</td></tr> :
                            releases.map(r => (
                              <tr key={r.id}>
                                <td><strong>{r.version}</strong></td>
                                <td><code style={{ fontSize: 11 }}>{r.file_name || r.file_path || '-'}</code> <span className="text-muted">{formatBytes(r.file_size)}</span>{!r.stored_in_drive && r.file_path && <span className="badge bg-warning text-dark ms-1" title="Guardada en el disco del servidor (se pierde al desplegar). Súbela de nuevo.">en disco</span>}</td>
                                <td style={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>{r.changelog || '-'}</td>
                                <td>{r.requires_wp || '-'}</td>
                                <td>{r.requires_php || '-'}</td>
                                <td>
                                  <button className={`btn btn-sm ${r.is_published ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => togglePublished(r)} title="Cambiar publicada / oculta">{r.is_published ? 'Sí' : 'No'}</button>
                                </td>
                                <td>{r.downloads_count}</td>
                                <td>{formatDate(r.created_at)}</td>
                                <td className="text-end text-nowrap">
                                  <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openModal('release', { ...emptyRelease, ...r, requires_wp: r.requires_wp || '', tested_up_to: r.tested_up_to || '', requires_php: r.requires_php || '', changelog: r.changelog || '' })}>Editar</button>
                                  {isAdmin && <button className="btn btn-sm btn-outline-danger" onClick={() => deleteRelease(r)}>Borrar</button>}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {tab === 'licenses' && (
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2 gap-2">
                  <input className="form-control form-control-sm" style={{ maxWidth: 320 }} placeholder="Buscar por clave, email, cliente o producto" value={licenseSearch} onChange={(e) => setLicenseSearch(e.target.value)} />
                  <button className="btn btn-primary btn-sm" onClick={() => openModal('license', { ...emptyLicense })}>Crear licencia manual</button>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-striped align-middle">
                    <thead><tr><th>Clave</th><th>Cliente</th><th>Producto</th><th>Plan</th><th>Estado</th><th>Caduca / actualiz. hasta</th><th>Sitios</th><th>Email</th><th></th></tr></thead>
                    <tbody>
                      {!visibleLicenses.length ? <tr><td colSpan="9" className="text-center text-muted py-3">Sin licencias</td></tr> :
                        visibleLicenses.map(l => (
                          <React.Fragment key={l.id}>
                            <tr>
                              <td><code style={{ fontSize: 11 }}>{l.license_key}</code></td>
                              <td>{l.client_name ? <>{l.client_name}<br /><span className="text-muted">{l.customer_email}</span></> : l.customer_email}</td>
                              <td>{l.product_name}</td>
                              <td>{l.plan_name} <span className="text-muted">({BILLING[l.billing_model] || l.billing_model})</span></td>
                              <td>{statusBadge(l.status, LICENSE_STATUS)}</td>
                              <td>{l.expires_at ? formatDate(l.expires_at) : 'Sin caducidad'}</td>
                              <td>{l.activations_used}/{l.activation_limit}</td>
                              <td>{l.notified_at ? <span className="text-success" title={`Enviado el ${formatDate(l.notified_at)}`}>Enviado</span> : <span className="text-warning">Sin enviar</span>}{!l.has_portal_access && <div className="text-muted" style={{ fontSize: 11 }}>sin acceso al portal</div>}</td>
                              <td className="text-end text-nowrap">
                                <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => toggleActivations(l.id)}>{expandedLicense === l.id ? 'Ocultar' : 'Sitios'}</button>
                                <div className="btn-group">
                                  <button className="btn btn-sm btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown">Acciones</button>
                                  <ul className="dropdown-menu dropdown-menu-end">
                                    <li><button className="dropdown-item" onClick={() => notifyLicense(l)}>{l.notified_at ? 'Reenviar email con la clave' : 'Enviar email con la clave'}</button></li>
                                    <li><button className="dropdown-item" onClick={() => openModal('licenseEdit', { ...emptyLicenseEdit, id: l.id, activation_limit: l.activation_limit, expires_at: toInputDate(l.expires_at), notes: l.notes || '' })}>Editar (sitios, fecha, notas)</button></li>
                                    <li><button className="dropdown-item" onClick={() => extendLicense(l)}>Prorrogar 12 meses</button></li>
                                    <li><hr className="dropdown-divider" /></li>
                                    {l.status !== 'active' && <li><button className="dropdown-item" onClick={() => setLicenseStatus(l, 'active')}>Reactivar</button></li>}
                                    {l.status === 'active' && <li><button className="dropdown-item" onClick={() => setLicenseStatus(l, 'suspended')}>Suspender</button></li>}
                                    {l.status !== 'revoked' && <li><button className="dropdown-item text-danger" onClick={() => setLicenseStatus(l, 'revoked')}>Revocar</button></li>}
                                  </ul>
                                </div>
                              </td>
                            </tr>
                            {expandedLicense === l.id && (
                              <tr>
                                <td colSpan="9" style={{ background: '#f8f9fa', padding: 12 }}>
                                  {l.notes && <div className="text-muted mb-2">Notas: {l.notes}</div>}
                                  {!activations[l.id] ? <span className="text-muted">Cargando...</span> :
                                    !activations[l.id].length ? <span className="text-muted">Todavía no se ha activado en ningún sitio.</span> :
                                    <table className="table table-sm mb-0">
                                      <thead><tr><th>Sitio</th><th>Estado</th><th>Versión del plugin</th><th>Activado</th><th>Última comprobación</th><th></th></tr></thead>
                                      <tbody>
                                        {activations[l.id].map(a => (
                                          <tr key={a.id}>
                                            <td>{a.site_url}</td>
                                            <td>{a.status === 'active' ? <span className="badge bg-success">Activa</span> : <span className="badge bg-secondary">Liberada</span>}</td>
                                            <td>{a.plugin_version || '-'}{a.wp_version ? <span className="text-muted"> · WP {a.wp_version}</span> : ''}</td>
                                            <td>{formatDate(a.activated_at)}</td>
                                            <td>{formatDate(a.last_check_at)}</td>
                                            <td className="text-end">{a.status === 'active' && <button className="btn btn-sm btn-outline-secondary" onClick={() => freeSite(l, a)}>Liberar sitio</button>}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  }
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === 'orders' && (
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm table-striped align-middle">
                    <thead><tr><th>ID</th><th>Email</th><th>Producto</th><th>Plan</th><th>Estado</th><th>Total</th><th>Origen</th><th>Licencia</th><th>Fecha</th></tr></thead>
                    <tbody>
                      {!orders.length ? <tr><td colSpan="9" className="text-center text-muted py-3">Sin pedidos</td></tr> :
                        orders.map(o => (
                          <tr key={o.id}>
                            <td>#{o.id}</td>
                            <td>{o.customer_email}</td>
                            <td>{o.product_name || o.product_slug}</td>
                            <td>{o.plan_name || o.plan_slug}</td>
                            <td>{statusBadge(o.status, ORDER_STATUS)}</td>
                            <td>{o.amount_total ? `${Number(o.amount_total).toFixed(2)} ${o.currency || 'EUR'}` : '-'}</td>
                            <td>{o.source_channel === 'web_stripe' ? 'Stripe' : `Manual${o.source_reference ? ` (${o.source_reference})` : ''}`}</td>
                            <td><code style={{ fontSize: 11 }}>{o.license_key || '-'}</code></td>
                            <td>{formatDate(o.paid_at || o.created_at)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === 'settings' && settings && (
            <div className="row g-3">
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5>Conexión con los plugins</h5>
                    <p className="text-muted">Cada plugin lleva dentro la dirección de la API y el secreto HMAC para firmar las peticiones de licencia. Cópialos en la configuración del plugin antes de empaquetarlo.</p>
                    <label className="form-label small">Dirección de la API</label>
                    <div className="input-group input-group-sm mb-3">
                      <input className="form-control" readOnly value={settings.api_base_url} />
                      <button className="btn btn-outline-secondary" type="button" onClick={() => copy(settings.api_base_url)}>Copiar</button>
                    </div>
                    <label className="form-label small">Secreto HMAC {settings.hmac_from_env && <span className="badge bg-warning text-dark ms-1" title="Se está usando el secreto del servidor (variable de entorno). Genera uno propio para el módulo.">heredado del servidor</span>}</label>
                    <div className="input-group input-group-sm mb-2">
                      <input className="form-control font-monospace" readOnly value={settings.hmac_secret || ''} placeholder="Sin secreto: los plugins no podrán activar licencias" />
                      <button className="btn btn-outline-secondary" type="button" disabled={!settings.hmac_secret} onClick={() => copy(settings.hmac_secret)}>Copiar</button>
                      <button className="btn btn-outline-primary" type="button" onClick={generateHmac}>{settings.hmac_secret ? 'Generar nuevo' : 'Generar'}</button>
                    </div>
                    <p className="text-muted mb-3" style={{ fontSize: 12 }}>Si generas uno nuevo, los plugins ya instalados con el anterior dejarán de validar hasta que actualicen.</p>
                    <label className="form-label small">Usar un secreto ya existente <span className="text-muted">(por ejemplo, el que ya lleva un plugin empaquetado)</span></label>
                    <form className="input-group input-group-sm" onSubmit={async (e) => { e.preventDefault(); if (!settingsForm.hmac_paste) return; await confirmAnd('¿Guardar este secreto HMAC? Sustituye al actual.', async () => { await api('/api/software/admin/settings', json('PUT', { hmac_secret: settingsForm.hmac_paste.trim() })); setSettingsForm({ ...settingsForm, hmac_paste: '' }); await fetchSettings(); flash('Secreto guardado.'); }); }}>
                      <input className="form-control font-monospace" type="password" placeholder="Pega aquí el secreto" value={settingsForm.hmac_paste || ''} onChange={(e) => setSettingsForm({ ...settingsForm, hmac_paste: e.target.value })} />
                      <button className="btn btn-outline-secondary" type="submit" disabled={!settingsForm.hmac_paste}>Guardar</button>
                    </form>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <h5>Stripe (venta desde la web)</h5>
                    <p className="text-muted">Claves propias de este módulo. Si se dejan vacías se usan las de facturación. Webhook a registrar en Stripe: <code>{settings.stripe_webhook_url}</code> (eventos checkout.session.completed, invoice.paid, invoice.payment_failed, charge.refunded, customer.subscription.updated y customer.subscription.deleted).</p>
                    <form onSubmit={saveSettings}>
                      <div className="mb-2">
                        <label className="form-label small">Modo</label>
                        <select className="form-select form-select-sm" value={settingsForm.mode} onChange={(e) => setSettingsForm({ ...settingsForm, mode: e.target.value })}>
                          <option value="test">Pruebas (test)</option>
                          <option value="live">Real (live)</option>
                        </select>
                      </div>
                      {['test', 'live'].map((m) => (
                        <fieldset key={m} className="border rounded p-2 mb-2">
                          <legend className="float-none w-auto px-1 small mb-0">{m === 'test' ? 'Pruebas' : 'Real'}</legend>
                          <input className="form-control form-control-sm mb-1" placeholder="Clave publicable (pk_...)" value={settingsForm[`${m}_publishable_key`] || ''} onChange={(e) => setSettingsForm({ ...settingsForm, [`${m}_publishable_key`]: e.target.value })} />
                          <input className="form-control form-control-sm mb-1" type="password" placeholder={settings[`has_${m}_secret_key`] ? 'Clave secreta guardada (escribe para cambiarla)' : 'Clave secreta (sk_...)'} value={settingsForm[`${m}_secret_key`] || ''} onChange={(e) => setSettingsForm({ ...settingsForm, [`${m}_secret_key`]: e.target.value })} />
                          <input className="form-control form-control-sm" type="password" placeholder={settings[`has_${m}_webhook_secret`] ? 'Secreto del webhook guardado (escribe para cambiarlo)' : 'Secreto del webhook (whsec_...)'} value={settingsForm[`${m}_webhook_secret`] || ''} onChange={(e) => setSettingsForm({ ...settingsForm, [`${m}_webhook_secret`]: e.target.value })} />
                        </fieldset>
                      ))}
                      <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {modalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalType === 'product' && (form.id ? 'Editar producto' : 'Nuevo producto')}
                  {modalType === 'plan' && (form.id ? 'Editar plan' : 'Nuevo plan')}
                  {modalType === 'release' && (form.id ? `Editar versión ${form.version}` : 'Subir versión')}
                  {modalType === 'license' && 'Crear licencia manual'}
                  {modalType === 'licenseEdit' && 'Editar licencia'}
                </h5>
                <button className="btn-close" onClick={() => setModalOpen(false)}></button>
              </div>
              <form onSubmit={submitForm}>
                <div className="modal-body">

                  {modalType === 'product' && (<>
                    <div className="mb-2">
                      <label className="form-label small">Slug * <span className="text-muted">(identificador que lleva el plugin; minúsculas, números y guiones)</span></label>
                      <input className="form-control form-control-sm" value={form.slug} onChange={e => updateForm('slug', e.target.value)} required pattern="[a-z0-9][a-z0-9_-]{1,118}" placeholder="mi-plugin" />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Nombre *</label>
                      <input className="form-control form-control-sm" value={form.name} onChange={e => updateForm('name', e.target.value)} required />
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small">Tipo *</label>
                        <select className="form-select form-select-sm" value={form.type} onChange={e => updateForm('type', e.target.value)}>
                          {Object.entries(PRODUCT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Estado</label>
                        <select className="form-select form-select-sm" value={form.status} onChange={e => updateForm('status', e.target.value)}>
                          {Object.entries(PRODUCT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Descripción</label>
                      <textarea className="form-control form-control-sm" rows="2" value={form.description || ''} onChange={e => updateForm('description', e.target.value)} />
                    </div>
                  </>)}

                  {modalType === 'plan' && (<>
                    <div className="mb-2">
                      <label className="form-label small">Producto *</label>
                      <select className="form-select form-select-sm" value={form.product_id} onChange={e => updateForm('product_id', e.target.value)} required disabled={Boolean(form.id)}>
                        <option value="">Seleccionar...</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small">Slug *</label>
                        <input className="form-control form-control-sm" value={form.slug} onChange={e => updateForm('slug', e.target.value)} required pattern="[a-z0-9][a-z0-9_-]{1,118}" placeholder="anual" />
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Nombre *</label>
                        <input className="form-control form-control-sm" value={form.name} onChange={e => updateForm('name', e.target.value)} required />
                      </div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small">Facturación</label>
                        <select className="form-select form-select-sm" value={form.billing_model} onChange={e => updateForm('billing_model', e.target.value)}>
                          {Object.entries(BILLING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Precio (sin IVA) *</label>
                        <input className="form-control form-control-sm" type="number" step="0.01" min="0" value={form.price_amount} onChange={e => updateForm('price_amount', e.target.value)} required />
                      </div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-4">
                        <label className="form-label small">Moneda</label>
                        <input className="form-control form-control-sm" value={form.currency} onChange={e => updateForm('currency', e.target.value)} maxLength={3} />
                      </div>
                      <div className="col-4">
                        <label className="form-label small">Sitios permitidos</label>
                        <input className="form-control form-control-sm" type="number" min="1" value={form.activation_limit} onChange={e => updateForm('activation_limit', e.target.value)} />
                      </div>
                      <div className="col-4 d-flex align-items-end">
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" checked={form.is_active} onChange={e => updateForm('is_active', e.target.checked)} id="planActive" />
                          <label className="form-check-label small" htmlFor="planActive">Activo</label>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-3 mb-2">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={form.updates_included !== false} onChange={e => updateForm('updates_included', e.target.checked)} id="planUpdates" />
                        <label className="form-check-label small" htmlFor="planUpdates">Incluye actualizaciones</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={form.support_included !== false} onChange={e => updateForm('support_included', e.target.checked)} id="planSupport" />
                        <label className="form-check-label small" htmlFor="planSupport">Incluye soporte</label>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Precio en Stripe (price_...) <span className="text-muted">solo para vender desde la web</span></label>
                      <input className="form-control form-control-sm" value={form.stripe_price_id} onChange={e => updateForm('stripe_price_id', e.target.value)} placeholder="price_..." />
                    </div>
                  </>)}

                  {modalType === 'release' && (<>
                    {!form.id && (<>
                      <div className="mb-2">
                        <label className="form-label small">Producto *</label>
                        <select className="form-select form-select-sm" value={form.product_id} onChange={e => updateForm('product_id', e.target.value)} required>
                          <option value="">Seleccionar...</option>
                          {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-5">
                          <label className="form-label small">Versión *</label>
                          <input className="form-control form-control-sm" value={form.version} onChange={e => updateForm('version', e.target.value)} required placeholder="1.0.0" />
                        </div>
                        <div className="col-7">
                          <label className="form-label small">Archivo .zip *</label>
                          <input className="form-control form-control-sm" type="file" accept=".zip,application/zip" onChange={e => updateForm('file', e.target.files?.[0] || null)} required />
                        </div>
                      </div>
                      <p className="text-muted" style={{ fontSize: 12 }}>La versión debe coincidir con la cabecera «Version» del plugin dentro del zip; si no, WordPress la ofrecería una y otra vez.</p>
                    </>)}
                    <div className="mb-2">
                      <label className="form-label small">Cambios de esta versión (lo ve el cliente)</label>
                      <textarea className="form-control form-control-sm" rows="3" value={form.changelog} onChange={e => updateForm('changelog', e.target.value)} />
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-4">
                        <label className="form-label small">Requiere WP</label>
                        <input className="form-control form-control-sm" value={form.requires_wp} onChange={e => updateForm('requires_wp', e.target.value)} placeholder="6.5" />
                      </div>
                      <div className="col-4">
                        <label className="form-label small">Probado hasta</label>
                        <input className="form-control form-control-sm" value={form.tested_up_to} onChange={e => updateForm('tested_up_to', e.target.value)} placeholder="6.7" />
                      </div>
                      <div className="col-4">
                        <label className="form-label small">Requiere PHP</label>
                        <input className="form-control form-control-sm" value={form.requires_php} onChange={e => updateForm('requires_php', e.target.value)} placeholder="8.1" />
                      </div>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" checked={form.is_published} onChange={e => updateForm('is_published', e.target.checked)} id="relPublished" />
                      <label className="form-check-label small" htmlFor="relPublished">Publicada (los clientes la reciben como actualización)</label>
                    </div>
                  </>)}

                  {modalType === 'license' && (<>
                    <div className="mb-2">
                      <label className="form-label small">Email del cliente *</label>
                      <input className="form-control form-control-sm" type="email" value={form.email} onChange={e => updateForm('email', e.target.value)} required />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Nombre o empresa <span className="text-muted">(si es un cliente nuevo se crea su ficha)</span></label>
                      <input className="form-control form-control-sm" value={form.customer_name} onChange={e => updateForm('customer_name', e.target.value)} />
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small">Producto *</label>
                        <select className="form-select form-select-sm" value={form.product_slug} onChange={e => { updateForm('product_slug', e.target.value); updateForm('plan_slug', ''); }} required>
                          <option value="">Seleccionar...</option>
                          {products.map(p => <option key={p.id} value={p.slug}>{p.name}</option>)}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Plan *</label>
                        <select className="form-select form-select-sm" value={form.plan_slug} onChange={e => updateForm('plan_slug', e.target.value)} required disabled={!form.product_slug}>
                          <option value="">Seleccionar...</option>
                          {plansOfSelectedProduct.map(p => <option key={p.id} value={p.slug}>{p.name} ({BILLING[p.billing_model]}, {p.activation_limit} {p.activation_limit === 1 ? 'sitio' : 'sitios'})</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small">Caduca / actualizaciones hasta</label>
                        <input className="form-control form-control-sm" type="date" value={form.expires_at} onChange={e => updateForm('expires_at', e.target.value)} />
                        <div className="text-muted" style={{ fontSize: 11 }}>Vacío = un año desde hoy</div>
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Sitios permitidos</label>
                        <input className="form-control form-control-sm" type="number" min="1" value={form.activation_limit} onChange={e => updateForm('activation_limit', e.target.value)} placeholder="los del plan" />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Referencia del pago o pedido</label>
                      <input className="form-control form-control-sm" value={form.source_reference} onChange={e => updateForm('source_reference', e.target.value)} placeholder="Dolistore #123, factura F-2026-045, presupuesto PR-0650…" />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Notas internas</label>
                      <textarea className="form-control form-control-sm" rows="2" value={form.notes} onChange={e => updateForm('notes', e.target.value)} />
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" checked={form.send_email} onChange={e => updateForm('send_email', e.target.checked)} id="licSend" />
                      <label className="form-check-label small" htmlFor="licSend">Enviar al cliente el email con la clave (y crear su acceso al área de cliente si no lo tiene)</label>
                    </div>
                  </>)}

                  {modalType === 'licenseEdit' && (<>
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small">Sitios permitidos</label>
                        <input className="form-control form-control-sm" type="number" min="1" value={form.activation_limit} onChange={e => updateForm('activation_limit', e.target.value)} required />
                      </div>
                      <div className="col-6">
                        <label className="form-label small">Caduca / actualizaciones hasta</label>
                        <input className="form-control form-control-sm" type="date" value={form.expires_at} onChange={e => updateForm('expires_at', e.target.value)} />
                        <div className="text-muted" style={{ fontSize: 11 }}>Vacío = sin caducidad</div>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label small">Notas internas</label>
                      <textarea className="form-control form-control-sm" rows="3" value={form.notes} onChange={e => updateForm('notes', e.target.value)} />
                    </div>
                  </>)}

                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModalOpen(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? (modalType === 'release' && !form.id ? 'Subiendo...' : 'Guardando...') : 'Guardar'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoftwarePage;
